import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, apiErrorText, formatDate, formatBRL, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import Attachments from "@/components/Attachments";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FilePdf, MagicWand } from "@phosphor-icons/react";

const STATUS = {
  draft: { label: "Rascunho", cls: "bg-muted text-muted-foreground" },
  sent: { label: "Enviado", cls: "bg-accent/20 text-accent" },
  approved: { label: "Aprovado", cls: "bg-green-500/20 text-green-400" },
  signed: { label: "Assinado", cls: "bg-primary/20 text-primary" },
  rejected: { label: "Recusado", cls: "bg-destructive/20 text-destructive" },
};

function ExternalAttachments({ type, clients }) {
  const [selectedClient, setSelectedClient] = useState("");
  return (
    <div>
      <div className="tech-card p-4 mb-6 flex items-end gap-4 max-w-lg">
        <div className="flex-1 space-y-2">
          <Label className="label-tech">Cliente</Label>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger data-testid={`external-${type}-client`}><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {!selectedClient ? (
        <div className="tech-card p-12 text-center">
          <div className="label-tech text-muted-foreground">Selecione um cliente para anexar {type === "contract" ? "contratos" : "propostas"} externas</div>
        </div>
      ) : (
        <Attachments clientId={selectedClient} category={type} title={type === "contract" ? "Contratos anexados" : "Propostas anexadas"} />
      )}
    </div>
  );
}

export default function DocumentsPage({ type }) {
  // type = 'contract' | 'proposal'
  const label = type === "contract" ? "Contratos" : "Propostas";
  const singular = type === "contract" ? "Contrato" : "Proposta";

  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    client_id: "", inspection_id: "", template_id: "", title: "", content: "",
    type, amount: "", status: "draft", valid_until: "", notes: "",
  });
  const [companyName, setCompanyName] = useState("");

  const load = async () => {
    const [d, c, ins, t] = await Promise.all([
      api.get("/documents", { params: { type } }),
      api.get("/clients"),
      api.get("/inspections"),
      api.get("/templates", { params: { type } }),
    ]);
    setRows(d.data); setClients(c.data); setInspections(ins.data); setTemplates(t.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const openNew = () => {
    setEditing(null);
    setForm({
      client_id: "", inspection_id: "", template_id: templates[0]?.id || "",
      title: "", content: "", type, amount: "", status: "draft", valid_until: "", notes: "",
    });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...r,
      amount: r.amount ?? "",
      inspection_id: r.inspection_id || "",
      template_id: r.template_id || "",
      valid_until: r.valid_until ? r.valid_until.slice(0, 10) : "",
    });
    setOpen(true);
  };

  const fillFromTemplate = async () => {
    if (!form.template_id || !form.client_id) {
      toast.error("Selecione o modelo e o cliente");
      return;
    }
    try {
      const { data } = await api.post("/documents/render", {
        template_id: form.template_id,
        client_id: form.client_id,
        inspection_id: form.inspection_id || null,
        variables: {
          company_name: companyName || "Sua Empresa de Drones",
          amount: form.amount || 0,
          valid_until: form.valid_until ? formatDate(form.valid_until) : "",
        },
      });
      setForm((f) => ({ ...f, content: data.content, title: f.title || data.title }));
      toast.success("Modelo preenchido");
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        amount: form.amount === "" ? null : parseFloat(form.amount),
        inspection_id: form.inspection_id || null,
        template_id: form.template_id || null,
        valid_until: form.valid_until || null,
      };
      if (editing) await api.put(`/documents/${editing.id}`, payload);
      else await api.post("/documents", payload);
      toast.success("Salvo"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorText(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover?")) return;
    await api.delete(`/documents/${id}`); toast.success("Removido"); load();
  };

  const openPdf = (id) => window.open(`${API}/documents/${id}/pdf`, "_blank");
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <Layout
      title={label}
      subtitle={type === "contract" ? "Gere contratos a partir de modelos, use o editor rico ou anexe contratos feitos fora do ERP" : "Propostas comerciais em PDF a partir de modelos, editor rico ou anexos externos"}
      actions={<Button onClick={openNew} data-testid={`new-${type}-btn`} className="gap-2"><Plus size={16} weight="bold" /> {`Novo ${singular.toLowerCase()}`}</Button>}
    >
      <Tabs defaultValue="generated" className="w-full">
        <TabsList className="mb-6" data-testid={`${type}-tabs`}>
          <TabsTrigger value="generated" data-testid={`tab-${type}-generated`}>Gerados no ERP ({rows.length})</TabsTrigger>
          <TabsTrigger value="attachments" data-testid={`tab-${type}-attachments`}>Anexados de fora</TabsTrigger>
        </TabsList>

        <TabsContent value="generated">
          <div className="tech-card overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="label-tech text-muted-foreground p-4">Nº</th>
                  <th className="label-tech text-muted-foreground p-4">Título</th>
                  <th className="label-tech text-muted-foreground p-4">Cliente</th>
                  <th className="label-tech text-muted-foreground p-4">Valor</th>
                  <th className="label-tech text-muted-foreground p-4">Data</th>
                  <th className="label-tech text-muted-foreground p-4">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody data-testid={`${type}-list`}>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">Nenhum {singular.toLowerCase()} cadastrado</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-b border-border hover:bg-secondary/30">
                    <td className="p-4 mono text-sm">{r.number || "—"}</td>
                    <td className="p-4 font-bold">{r.title}</td>
                    <td className="p-4 text-sm">{clientName(r.client_id)}</td>
                    <td className="p-4 text-sm mono">{r.amount ? formatBRL(r.amount) : "—"}</td>
                    <td className="p-4 text-sm mono">{formatDate(r.created_at)}</td>
                    <td className="p-4"><Badge className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label}</Badge></td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openPdf(r.id)} title="Ver PDF" data-testid={`pdf-${type}-${r.id}`}>
                          <FilePdf size={16} className="text-accent" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><PencilSimple size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash size={16} className="text-destructive" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="attachments">
          <ExternalAttachments type={type} clients={clients} />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Editar ${singular.toLowerCase()}` : `Novo ${singular.toLowerCase()}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="label-tech">Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid={`${type}-client`}><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Inspeção (opcional)</Label>
              <Select value={form.inspection_id || "none"} onValueChange={(v) => setForm({ ...form, inspection_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Nenhuma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {inspections.filter((i) => !form.client_id || i.client_id === form.client_id).map((i) => <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Modelo</Label>
              <Select value={form.template_id || "none"} onValueChange={(v) => setForm({ ...form, template_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid={`${type}-template`}><SelectValue placeholder="Nenhum" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Valor (R$)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid={`${type}-amount`} />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Validade</Label>
              <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="label-tech">Título *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={`${type}-title`} placeholder={`Título do ${singular.toLowerCase()}`} />
            </div>
            <div className="space-y-2">
              <Label className="label-tech">Sua empresa (para o modelo)</Label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Nome da sua empresa" />
            </div>
            <div className="md:col-span-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="label-tech">Conteúdo</Label>
                <Button type="button" variant="outline" size="sm" onClick={fillFromTemplate} className="gap-2" data-testid={`${type}-fill-template`}>
                  <MagicWand size={14} weight="bold" /> Preencher a partir do modelo
                </Button>
              </div>
              <RichTextEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} testId={`${type}-editor`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid={`save-${type}-btn`}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
